// Task Categories Management
import { db } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

// Default task categories with their default due date durations
const defaultTaskCategories = [
  { name: 'upholstery', dueDays: 5 },
  { name: 'delivery', dueDays: 3 },
  { name: 'fabric', dueDays: 5 },
  { name: 'furniture', dueDays: 7 },
  { name: 'installation', dueDays: 3 },
  { name: 'design', dueDays: 7 },
  { name: 'consultation', dueDays: 2 },
  { name: 'lighting', dueDays: 3 },
  { name: 'other', dueDays: 5 }
];

// Get task categories from Firestore or return defaults
export const getTaskCategories = async () => {
  try {
    const categoriesRef = doc(db, 'settings', 'taskCategories');
    const categoriesDoc = await getDoc(categoriesRef);
    
    if (categoriesDoc.exists()) {
      const data = categoriesDoc.data();
      // Handle both old (array) and new (object) formats
      if (Array.isArray(data.categories)) {
        // Convert old format to new format
        return data.categories.map(cat => 
          typeof cat === 'string' ? { name: cat, dueDays: 5 } : cat
        );
      }
      return data.categories || defaultTaskCategories;
    }
    
    // If no categories exist in Firestore, return defaults
    return defaultTaskCategories;
  } catch (error) {
    console.error('Error getting task categories:', error);
    return defaultTaskCategories;
  }
};

// Save task categories to Firestore
export const saveTaskCategories = async (categories) => {
  try {
    const categoriesRef = doc(db, 'settings', 'taskCategories');
    
    await setDoc(categoriesRef, {
      categories: categories,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error saving task categories:', error);
    throw error;
  }
};

// Add a new task category
export const addTaskCategory = async (categoryName, dueDays = 5) => {
  try {
    const currentCategories = await getTaskCategories();
    const normalizedCategory = categoryName.toLowerCase().trim();
    
    // Check if category already exists
    if (currentCategories.some(cat => cat.name === normalizedCategory)) {
      throw new Error('Category already exists');
    }
    
    const updatedCategories = [...currentCategories, { name: normalizedCategory, dueDays }];
    await saveTaskCategories(updatedCategories);
    
    return updatedCategories;
  } catch (error) {
    console.error('Error adding task category:', error);
    throw error;
  }
};

// Remove a task category
export const removeTaskCategory = async (categoryName) => {
  try {
    const currentCategories = await getTaskCategories();
    const updatedCategories = currentCategories.filter(cat => 
      typeof cat === 'string' ? cat !== categoryName : cat.name !== categoryName
    );
    
    await saveTaskCategories(updatedCategories);
    
    return updatedCategories;
  } catch (error) {
    console.error('Error removing task category:', error);
    throw error;
  }
};

// Update a task category's due days
export const updateCategoryDueDays = async (categoryName, dueDays) => {
  try {
    const currentCategories = await getTaskCategories();
    const updatedCategories = currentCategories.map(cat => {
      if (typeof cat === 'string') {
        return cat === categoryName ? { name: cat, dueDays } : { name: cat, dueDays: 5 };
      }
      return cat.name === categoryName ? { ...cat, dueDays } : cat;
    });
    
    await saveTaskCategories(updatedCategories);
    
    return updatedCategories;
  } catch (error) {
    console.error('Error updating category due days:', error);
    throw error;
  }
};

// Get due days for a specific category
export const getCategoryDueDays = async (categoryName) => {
  try {
    const categories = await getTaskCategories();
    const category = categories.find(cat => 
      typeof cat === 'string' ? cat === categoryName : cat.name === categoryName
    );
    
    if (typeof category === 'string') return 5; // Default
    return category ? category.dueDays : 5;
  } catch (error) {
    console.error('Error getting category due days:', error);
    return 5; // Default fallback
  }
};

// Format category for display (capitalize first letter)
export const formatCategoryForDisplay = (category) => {
  const name = typeof category === 'string' ? category : category.name;
  return name.charAt(0).toUpperCase() + name.slice(1);
};